/**
 * Content Planner Types
 *
 * Types for the WriterZen-style keyword research and clustering feature.
 */

// =============================================================================
// Enums
// =============================================================================

export type SearchIntent = 'informational' | 'commercial_investigation' | 'transactional' | 'navigational';

export type TopicalMapStatus =
  | 'pending'
  | 'fetching_keywords'
  | 'classifying'
  | 'clustering'
  | 'organizing'
  | 'completed'
  | 'failed';

export type ClusterStatus = 'new' | 'approved' | 'in_progress' | 'draft_ready' | 'published';

export type DifficultyLevel = 'very_easy' | 'easy' | 'medium' | 'hard' | 'very_hard';

export type ViewMode = 'list' | 'table' | 'calendar' | 'feed';

// =============================================================================
// Keyword Types
// =============================================================================

export interface KeywordData {
  id: string;
  keyword: string;
  monthly_search_volume: number;
  keyword_difficulty: number;
  cpc: number;
  intent: SearchIntent;
  trend: string;
  is_primary: boolean;
  is_selected?: boolean;
}

// =============================================================================
// Cluster Types
// =============================================================================

export interface ClusterSummary {
  id: string;
  name: string;
  total_volume: number;
  avg_difficulty: number;
  avg_cpc: number;
  keyword_count: number;
  primary_intent: SearchIntent;
  potential_traffic: number;
  status: ClusterStatus;
  assigned_url?: string;
  pillar_topic_id?: string;
  is_starred?: boolean;
  // Opportunity metrics (computed on-the-fly by backend)
  opportunity_score: number;
  fruit_tier: 'low' | 'mid' | 'high';
  // Linked blog content ID (set when content is created from this cluster)
  content_id?: string;
  // Inline keywords preview (first few + count)
  keywords_preview?: string[];
  // Transient, client-only: keywords the reviewer has unchecked in the planner
  // wizard so they are NOT sent as secondary_keywords for this generation run.
  // Not persisted to the topical map; lives only in wizard state.
  excluded_keywords?: string[];
  // Scheduled time for content generation and publishing (ISO 8601 string)
  scheduled_publish_at?: string | null;
}

export interface ClusterDetail extends ClusterSummary {
  article_suggestions?: string[];
  serp_urls?: string[];
  serp_features?: Record<string, boolean>;
  keywords: KeywordData[];
  created_at: string;
}

// =============================================================================
// Pillar Types
// =============================================================================

export interface PillarSummary {
  id: string;
  name: string;
  description?: string;
  total_volume: number;
  cluster_count: number;
  sort_order: number;
}

export interface PillarDetail extends PillarSummary {
  clusters: ClusterSummary[];
}

// =============================================================================
// Topical Map Types
// =============================================================================

export interface TopicalMapSummary {
  map_id: string;
  seed_keyword: string;
  target_url?: string;
  region: string;
  language: string;
  status: TopicalMapStatus;
  progress_percentage: number;
  current_stage?: string;
  total_clusters: number;
  total_keywords: number;
  total_volume: number;
  created_at: string;
  completed_at?: string;
  processing_log?: ProcessingLogEntry[] | null;
  processing_started_at?: string | null;
}

export interface TopicalMapDetail extends TopicalMapSummary {
  error_message?: string;
  clusters: ClusterSummary[];
}

// =============================================================================
// Request Types
// =============================================================================

export interface CreateTopicalMapRequest {
  seed_keyword: string;
  target_url?: string;
  region?: string;
  language?: string;
  max_keywords?: number;
  min_volume?: number;
  max_difficulty?: number;
  overlap_threshold?: number;
}

export interface UpdateClusterRequest {
  status?: ClusterStatus;
  assigned_url?: string;
  pillar_topic_id?: string | null;
  is_starred?: boolean;
}

export interface BulkUpdateClusterStatusRequest {
  cluster_ids: string[];
  status: ClusterStatus;
}

// =============================================================================
// Response Types
// =============================================================================

export interface TopicalMapListResponse {
  maps: TopicalMapSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface ClusterListResponse {
  clusters: ClusterSummary[];
  total: number;
}

// =============================================================================
// Keyword Feed Types
// =============================================================================

export type KeywordFeedStatus =
  | 'candidate'
  | 'approved'
  | 'rejected'
  | 'queued'
  | 'generated'
  | 'skipped'
  | 'stale';

export type KeywordFeedDuplicateStatus =
  | 'clear'
  | 'exact_duplicate'
  | 'near_duplicate';

/**
 * Advisory-only Codex/Hermes vetting verdict on a keyword feed candidate.
 * Never folds into `status` — human Approve/Reject stays the sole gate.
 */
export type KeywordFeedHermesVerdict = 'agree' | 'weak' | 'swap';

export interface KeywordFeedTargetKeyword {
  keyword: string;
  search_volume?: number | null;
  keyword_difficulty?: number | null;
  cpc?: number | null;
  is_primary?: boolean;
}

export interface KeywordFeedItem {
  id: string;
  tenant_id: string;
  topical_map_id: string;
  cluster_id: string;
  primary_keyword: string;
  secondary_keywords: string[];
  map_name?: string | null;
  total_volume: number;
  avg_difficulty: number;
  avg_cpc: number;
  opportunity_score: number;
  similarity_score: number;
  status: KeywordFeedStatus;
  duplicate_status: KeywordFeedDuplicateStatus;
  filter_reason?: string | null;
  evidence: {
    similarity_threshold?: number;
    similarity_source?: {
      source_type?: string;
      source_id?: string;
      title?: string;
    } | null;
    [key: string]: unknown;
  };
  target_keywords: KeywordFeedTargetKeyword[];
  priority: number;
  notes?: string | null;
  generated_content_id?: string | null;
  /**
   * Content-planning month bucket this keyword is slated for, format
   * 'YYYY-MM' (e.g. "2026-08"). Null = unplanned. Auto-assigned to the next
   * calendar month on human Approve when unset; an explicit PATCH value
   * always wins. Advisory display only — never gates Approve/Generate.
   */
  target_month?: string | null;
  last_evaluated_at: string;
  created_at: string;
  updated_at: string;
  /** Advisory-only Codex/Hermes vetting verdict. Null/undefined = not yet vetted. */
  hermes_verdict?: KeywordFeedHermesVerdict | null;
  /** Suggested replacement keyword, present when hermes_verdict === 'swap'. */
  hermes_suggested_swap?: string | null;
  hermes_confidence?: number | null;
  hermes_reason?: string | null;
  hermes_judged_at?: string | null;
}

export interface KeywordFeedListParams {
  status?: KeywordFeedStatus;
  include_skipped?: boolean;
  limit?: number;
  offset?: number;
}

export interface KeywordFeedListResponse {
  items: KeywordFeedItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface KeywordFeedRefreshRequest {
  max_candidates?: number;
  max_items?: number;
  similarity_threshold?: number;
  min_opportunity_score?: number;
  include_skipped?: boolean;
}

export interface KeywordFeedRefreshResponse {
  items: KeywordFeedItem[];
  inserted: number;
  updated: number;
  skipped: number;
  total_candidates: number;
  internal_pages_index_count: number;
  index_auto_sync_attempted: boolean;
  similarity_threshold: number;
}

export interface KeywordFeedAhrefsRefreshRequest {
  seeds: string[];
  target_count?: number;
  per_seed_limit?: number;
  country?: string;
  target_url?: string | null;
  preferred_keywords?: string[];
  exclude_terms?: string[];
  min_volume?: number;
  max_difficulty?: number | null;
  similarity_threshold?: number;
  dry_run?: boolean;
  include_skipped?: boolean;
}

export interface KeywordFeedAhrefsCandidate {
  keyword: string;
  source_seed: string;
  monthly_search_volume: number;
  keyword_difficulty: number;
  cpc: number;
  opportunity_score: number;
  duplicate_status: KeywordFeedDuplicateStatus;
  filter_reason?: string | null;
  similarity_score: number;
  selected: boolean;
}

export interface KeywordFeedAhrefsRefreshResponse {
  tenant_id: string;
  dry_run: boolean;
  topical_map_id?: string | null;
  seed_keyword?: string | null;
  raw_ahrefs_rows: number;
  unique_ahrefs_keywords: number;
  selected_count: number;
  skipped_duplicate_count: number;
  excluded_count: number;
  feed_refresh?: KeywordFeedRefreshResponse | null;
  candidates: KeywordFeedAhrefsCandidate[];
}

export interface KeywordFeedUpdateRequest {
  status?: KeywordFeedStatus;
  priority?: number;
  notes?: string;
  /** Format 'YYYY-MM'. Omitted leaves the existing value untouched. */
  target_month?: string;
}

export interface KeywordFeedGenerateRequest {
  publish_immediately?: boolean;
  export_to_gdocs?: boolean;
  wordpress_publish_mode?: 'none' | 'draft' | 'publish';
  generation_mode?: string;
  model_override?: string;
  /** Blog template (recipe) id. Omit to let the backend apply its default. */
  template_id?: string;
}

export interface KeywordFeedGenerateResponse {
  content_id: string;
  feed_item_id: string;
  keyword: string;
  status: string;
  dispatch_mode: string;
  job_id?: string | null;
}

export type KeywordVettingJobStatus =
  | 'queued'
  | 'running'
  | 'cancel_requested'
  | 'succeeded'
  | 'partial'
  | 'failed'
  | 'cancelled';

export interface KeywordVettingWarning {
  feed_item_id?: string | null;
  keyword?: string | null;
  reason: string;
}

export interface KeywordVettingJobResponse {
  job_id: string;
  tenant_id: string;
  status: KeywordVettingJobStatus;
  feed_item_ids: string[];
  warnings: KeywordVettingWarning[];
  created_at: string;
  created_by: string;
}

export interface KeywordVettingJobItem {
  feed_item_id: string;
  keyword: string;
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  verdict?: KeywordFeedHermesVerdict | null;
  suggested_swap_keyword?: string | null;
  confidence?: number | null;
  reason?: string | null;
  error?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
}

export interface KeywordVettingJobDetail {
  job_id: string;
  tenant_id: string;
  status: KeywordVettingJobStatus;
  items_total: number;
  items_done: number;
  items_failed: number;
  agree_count: number;
  weak_count: number;
  swap_count: number;
  created_at: string;
  started_at?: string | null;
  finished_at?: string | null;
  created_by: string;
  items: KeywordVettingJobItem[];
}

export interface KeywordFeedConfig {
  enabled: boolean;
  max_feed_items: number;
  max_candidates: number;
  similarity_threshold: number;
  min_opportunity_score?: number | null;
  include_cluster_statuses: string[];
  auto_refresh_enabled: boolean;
  ahrefs_seeds: string[];
  ahrefs_country: string;
  ahrefs_target_count: number;
  ahrefs_per_seed_limit: number;
  ahrefs_min_volume: number;
  ahrefs_max_difficulty?: number | null;
  ahrefs_preferred_keywords: string[];
  ahrefs_exclude_terms: string[];
}

export interface CommonWord {
  word: string;
  count: number;
}

export interface TopicalMapStatistics {
  map_id: string;
  total_pillars: number;
  total_clusters: number;
  total_keywords: number;
  total_volume: number;
  avg_cluster_volume: number;
  intent_breakdown: Record<string, number>;
  difficulty_breakdown: Record<string, number>;
  status_breakdown: Record<string, number>;
  top_clusters_by_volume: ClusterSummary[];
}

// =============================================================================
// Filter Types
// =============================================================================

export interface ClusterFilters {
  status?: ClusterStatus;
  intent?: SearchIntent;
  min_volume?: number;
  max_difficulty?: number;
  is_starred?: boolean;
  search?: string;
}

// =============================================================================
// UI Helper Types
// =============================================================================

export const INTENT_LABELS: Record<SearchIntent, string> = {
  informational: 'I',
  commercial_investigation: 'C',
  transactional: 'T',
  navigational: 'N',
};

export const INTENT_COLORS: Record<SearchIntent, string> = {
  informational: 'blue',
  commercial_investigation: 'gold',
  transactional: 'green',
  navigational: 'cyan',
};

export const INTENT_FULL_LABELS: Record<SearchIntent, string> = {
  informational: 'Informational',
  commercial_investigation: 'Commercial',
  transactional: 'Transactional',
  navigational: 'Navigational',
};

export const getDifficultyLevel = (kd: number): DifficultyLevel => {
  if (kd <= 15) return 'very_easy';
  if (kd <= 30) return 'easy';
  if (kd <= 50) return 'medium';
  if (kd <= 70) return 'hard';
  return 'very_hard';
};

export const DIFFICULTY_LABELS: Record<DifficultyLevel, string> = {
  very_easy: 'Very easy',
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
  very_hard: 'Very hard',
};

export const DIFFICULTY_COLORS: Record<DifficultyLevel, string> = {
  very_easy: 'green',
  easy: 'lime',
  medium: 'gold',
  hard: 'orange',
  very_hard: 'red',
};

export const STATUS_LABELS: Record<ClusterStatus, string> = {
  new: 'New',
  approved: 'Approved',
  in_progress: 'In Progress',
  draft_ready: 'Draft Ready',
  published: 'Published',
};

export const STATUS_COLORS: Record<ClusterStatus, string> = {
  new: 'blue',
  approved: 'cyan',
  in_progress: 'processing',
  draft_ready: 'gold',
  published: 'green',
};

// Country options for region selector
export const REGION_OPTIONS = [
  { value: 'US', label: 'United States', flag: '🇺🇸' },
  { value: 'UK', label: 'United Kingdom', flag: '🇬🇧' },
  { value: 'CA', label: 'Canada', flag: '🇨🇦' },
  { value: 'AU', label: 'Australia', flag: '🇦🇺' },
  { value: 'DE', label: 'Germany', flag: '🇩🇪' },
  { value: 'FR', label: 'France', flag: '🇫🇷' },
  { value: 'ES', label: 'Spain', flag: '🇪🇸' },
  { value: 'IT', label: 'Italy', flag: '🇮🇹' },
  { value: 'NL', label: 'Netherlands', flag: '🇳🇱' },
  { value: 'BR', label: 'Brazil', flag: '🇧🇷' },
  { value: 'MX', label: 'Mexico', flag: '🇲🇽' },
  { value: 'IN', label: 'India', flag: '🇮🇳' },
  { value: 'JP', label: 'Japan', flag: '🇯🇵' },
];

// =============================================================================
// Title Suggestion Types
// =============================================================================

/**
 * Response from the SERP-powered title suggestion API.
 */
export interface TitleSuggestionResponse {
  /** The keyword used for generating suggestions */
  keyword: string;
  /** List of SEO-optimized title suggestions */
  suggestions: string[];
  /** Source of suggestions: 'serp', 'crew', 'llm', or 'pattern_fallback' */
  source: 'serp' | 'crew' | 'llm' | 'pattern_fallback';
  /** Number of competitors analyzed (0 if fallback was used) */
  competitor_count: number;
}

// =============================================================================
// Keyword Dedup Types
// =============================================================================

export interface KeywordMatch {
  id: string;
  keyword: string;
  title: string | null;
  status: string;
  created_at: string | null;
}

export interface KeywordCheckResponse {
  exists: boolean;
  matches: KeywordMatch[];
  skipped_reason: string | null;
}

// =============================================================================
// AI Topic Suggestion Types
// =============================================================================

export interface TopicSuggestion {
  keyword: string;
  rationale: string;
  exists: boolean;
  /** Relevance from the deterministic filter: "high" (seed match) | "medium" (brand-adjacent). */
  relevance?: string | null;
}

export interface TopicSuggestionRequest {
  seed_keywords: string[];
  /** contentType of the selected template, when known — listicle-framed
   * suggestions ("5 Signs...") are suppressed for non-listicle templates. */
  template_content_type?: string | null;
  brand_bible?: {
    niche?: string;
    services?: string[];
    target_audience?: string;
  } | null;
  existing_keywords?: string[];
  count?: number;
}

export interface TopicSuggestionResponse {
  suggestions: TopicSuggestion[];
  error?: string | null;
  skipped_reason?: string | null;
}

// =============================================================================
// Seed Keyword Analysis Types
// =============================================================================

export type SeedKeywordCategory = 'service' | 'problem' | 'informational' | 'competitor_gap';
export type SeedKeywordPriority = 'high' | 'medium' | 'low';

export interface SeedKeywordAnalysis {
  keyword: string;
  rationale: string;
  category: SeedKeywordCategory;
  priority: SeedKeywordPriority;
  already_researched: boolean;
  // Ahrefs-enriched metrics (undefined if unavailable)
  volume?: number;
  difficulty?: number;
  cpc?: number;
  opportunity_score?: number;
  fruit_tier?: 'low' | 'mid' | 'high';
}

export interface SeedKeywordAnalysisResponse {
  suggestions: SeedKeywordAnalysis[];
  existing_research_keywords: string[];
}

// Processing log entry for keyword research progress
export interface ProcessingLogEntry {
  ts: string;
  level: 'info' | 'warn' | 'error';
  step: string;
  msg: string;
  elapsed_ms: number;
}

// =============================================================================
// Keyword Quality Types
// =============================================================================

export interface KeywordQualityInput {
  keyword: string;
  volume: number;
  difficulty: number;
  cpc: number;
}

export interface KeywordQualityAssessment {
  keyword: string;
  score: number;
  grade: string;
  source: string;
  components?: {
    volume_normalized: number;
    difficulty_inverted: number;
    cpc_normalized: number;
  };
  status?: string;
  error?: string;
  skipped_reason?: string;
}

export interface KeywordQualityResponse {
  assessments: KeywordQualityAssessment[];
}

// =============================================================================
// Keyword Quality Grade Helpers
// =============================================================================

/** Convert keyword difficulty to letter grade (mirrors backend KeywordQualityService). */
export const getKeywordGrade = (difficulty: number): string => {
  if (difficulty <= 30) return 'A';
  if (difficulty <= 50) return 'B';
  if (difficulty <= 70) return 'C';
  if (difficulty <= 85) return 'D';
  return 'F';
};

export const GRADE_COLORS: Record<string, string> = {
  A: 'bg-green-500 text-white',
  B: 'bg-blue-500 text-white',
  C: 'bg-yellow-500 text-white',
  D: 'bg-orange-500 text-white',
  F: 'bg-red-500 text-white',
};

// =============================================================================
// Fruit Tier Helpers
// =============================================================================

export type FruitTier = 'low' | 'mid' | 'high';

export const FRUIT_TIER_LABELS: Record<FruitTier, string> = {
  low: 'Low Hanging',
  mid: 'Mid Hanging',
  high: 'High Hanging',
};

export const FRUIT_TIER_COLORS: Record<FruitTier, string> = {
  low: 'bg-green-100 text-green-800 border-green-300',
  mid: 'bg-amber-100 text-amber-800 border-amber-300',
  high: 'bg-red-100 text-red-800 border-red-300',
};

// ── Calendar types ─────────────────────────────────────────────────────────

/**
 * Precomputed per-article issue count, attached to list-shaped content
 * responses by the backend. `null` when the count could not be computed
 * (e.g. missing generation_metadata). Mirrors the editor's IssueBadgeSummary
 * so the list badge and the editor badge read identically.
 *
 * Shape is locked by the cross-session contract (PR-2): backend Session A
 * produces it, frontend Session B consumes it verbatim.
 */
export interface IssueCount {
  /** Distinct fields with at least one issue (deduped per field). */
  total: number;
  /** Subset of `total` whose issues are block-severity. */
  blocking: number;
  /** `total - blocking` — review-severity issues only. */
  review: number;
}

export interface CalendarItem {
  id: string;
  tenant_id?: string | null;
  title: string | null;
  keyword: string | null;
  template_name: string | null;
  status: 'pending' | 'in_progress' | 'generating' | 'draft' | 'completed' | 'published' | 'failed' | string;
  publish_status: string | null;
  wordpress_status?: 'draft' | 'publish' | string | null;
  wordpress_post_id?: number | null;
  scheduled_publish_time?: string | null;
  /** True when the item has a real calendar placement (scheduled_publish_at/time
   * or publish_status === 'scheduled'). Unscheduled items are a work backlog and
   * are not bound to a calendar month. Computed by the backend get_calendar_items. */
  is_scheduled?: boolean;
  progress_percentage?: number | null;
  current_stage?: string | null;
  error_message?: string | null;
  wp_sync_status?: string | null;
  use_bridge?: boolean | null;
  generation_mode?: 'guide' | 'suggestion' | 'fully_automated' | 'supervised' | string | null;
  gdocs_doc_url?: string | null;
  /** Legacy row attribution; trustworthy only after generation is terminal. */
  model_used?: string | null;
  provider?: string | null;
  scheduled_date: string | null;  // YYYY-MM-DD
  volume: number | null;
  difficulty: number | null;
  grade: 'A' | 'B' | 'C' | 'D' | 'F' | null;
  quality_score: number | null;
  word_count: number | null;
  wp_url: string | null;
  created_at: string | null;
  editor_notes: string | null;
  /**
   * Precomputed per-article issue count from the list/summary/calendar
   * endpoints. Optional — `null`/absent when the backend could not compute
   * it (e.g. missing generation_metadata). The frontend NEVER fetches
   * ContentDetail per row to derive this; it only consumes the value
   * already on the list payload (no N+1).
   */
  issue_count?: IssueCount | null;
  /** UI-only: hex color set when displaying items from multiple workspaces simultaneously */
  _wsColor?: string;
  /** UI-only: tenant domain set when displaying multi-workspace items */
  _wsTenant?: string;
}

export interface CalendarStats {
  scheduled: number;
  draft: number;
  generating: number;
  published: number;
  failed: number;
  total: number;
  this_month?: number;
}

export interface CalendarResponse {
  items: CalendarItem[];
  stats: CalendarStats;
  total: number;
  limit: number;
  offset: number;
}

export interface ScheduleRequest {
  scheduled_date: string | null;  // YYYY-MM-DD or null to unschedule
}
